import { useState } from 'react';
import { ToastContainer } from 'react-toastify';
import AppointmentViewWrapper from '../AppointmentViewWrapper';
import { IAppointment } from '../../hooks/promises/getAppointments';
import { timeAgo, toLocalTime } from '../../utils/time';
import ErrorPage from '../../pages/ErrorPage';
import CommonButton from '../CommonButton';
import NoResultsFallback from '../NoResultsFallback';

interface IViewAppointmentsListProps {
  appointments: IAppointment[] | null;
  appointmentsError: boolean;
  deleteAppointment: () => void;
  setAppointmentSelectedForDeletion: React.Dispatch<
    React.SetStateAction<IAppointment | null>
  >;
  appointmentSelectedForDeletion: IAppointment | null;
}

function ViewAppointmentsList({
  appointments,
  deleteAppointment,
  setAppointmentSelectedForDeletion,
  appointmentSelectedForDeletion,
  appointmentsError,
}: IViewAppointmentsListProps) {
  const [showModal, setShowModal] = useState(false);

  if (appointmentsError || appointments === null) return <ErrorPage />;

  return (
    <>
      <div className="mb-8">
        {appointments?.length === 0 ? (
          <NoResultsFallback text="There are no existing appointments to view." />
        ) : (
          <div className="grid grid-cols-2 gap-6">
            {appointments?.map((x) => (
              <div
                className="relative bg-white rounded-md cols-span-1 shadow-md border-[1px] pb-16"
                key={x.appointmentId}
              >
                <div className="flex flex-col justify-between p-4">
                  <AppointmentViewWrapper
                    key={x.appointmentId}
                    appointment={x}
                  />
                  <div className="absolute bottom-0 right-0 flex items-center px-4 py-4">
                    <p className="mr-6 text-subtitle">
                      Created {timeAgo.format(toLocalTime(x.dateCreated))}
                    </p>
                    <CommonButton
                      variant="filled"
                      text="Delete"
                      onClick={() => {
                        setAppointmentSelectedForDeletion(x);
                        setShowModal(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div
          className={`${
            showModal ? 'visible' : 'invisible'
          } fixed top-0 bottom-0 left-0 right-0 z-40 h-full bg-black opacity-50`}
          onClickCapture={() => setShowModal(false)}
        />
        <div
          className={`${
            showModal ? 'visible' : 'invisible'
          } fixed top-0 bottom-0 left-0 right-0 z-50 m-auto w-[700px] h-[90%] bg-white rounded-lg shadow-lg`}
        >
          <div className="relative h-[100%] flex flex-col gap-2 pt-10 pb-4">
            <div className="pb-2">
              <h1 className="text-2xl font-semibold text-center">
                Delete appointment record?
              </h1>
              <p className="text-center text-subtitle">
                This action is irreversible.
              </p>
            </div>
            <div className="relative h-[65%] overflow-y-scroll">
              {appointmentSelectedForDeletion && (
                <AppointmentViewWrapper
                  appointment={appointmentSelectedForDeletion}
                />
              )}
            </div>
            <div className="flex flex-row justify-center gap-4 mt-12">
              <CommonButton
                variant="outline"
                text="Keep it"
                onClick={() => setShowModal(false)}
              />
              <CommonButton
                variant="filled"
                text="Delete it"
                onClick={deleteAppointment}
              />
            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
    </>
  );
}

export default ViewAppointmentsList;
